import {
  type Control,
  Controller,
  type FieldPath,
  type FieldValues,
} from 'react-hook-form';
import { Checkbox, type CheckboxProps } from 'antd';

import { extractPosFormItemProps } from './FormUtils.ts';
import { PosFormItem, type PosFormItemProps } from './PosFormItem.tsx';

type BasePosCheckboxProps = Omit<
  CheckboxProps,
  'onChange' | 'value' | 'name' | 'checked'
>;
type BasePosFormItemProps<TFieldValues extends FieldValues = FieldValues> =
  Omit<
    PosFormItemProps<TFieldValues>,
    'children' | 'control' | 'name' | 'valuePropName'
  >;

type ControlledCheckboxProps<TFieldValues extends FieldValues = FieldValues> =
  BasePosFormItemProps<TFieldValues> &
    BasePosCheckboxProps & {
      control: Control<TFieldValues>;
      name: FieldPath<TFieldValues>;
      value?: never;
      onChange?: never;
    };

type UncontrolledCheckboxProps = Partial<
  Record<keyof BasePosFormItemProps, never>
> &
  BasePosCheckboxProps & {
    control?: never;
    name?: string;
    value?: string;
    checked?: CheckboxProps['checked'];
    onChange: CheckboxProps['onChange'];
  };

export type PosCheckboxProps<TFieldValues extends FieldValues = FieldValues> =
  | ControlledCheckboxProps<TFieldValues>
  | UncontrolledCheckboxProps;

/**
 * @component
 * @description User checkbox with React-Hook-Form integration
 * @see {@link https://ant.design/components/checkbox | AntD Checkbox Component}
 *
 * @param PosCheckboxProps.control Control field from React-Hook-Form useForm
 * @remarks If an invalid control from useForm is passed then the Input will behave as an uncontrolled component and field name will not be type-safe
 * @param PosCheckboxProps.name Name of form field
 * @example
 * ```tsx
 * // Controlled (RHF) usage:
 * const { t } = useTranslation();
 * const { control } = useForm<FinanceForm>();
 *
 * <PosCheckbox
 *   control={control}
 *   name="taxExempt"
 * >
 *   {t('customers.customerDetails.taxExempt')}
 * </PosCheckbox>
 *
 * // Uncontrolled (No RHF) usage:
 * const [taxExempt, setTaxExempt] = useState<boolean>(false);
 *
 * <PosCheckbox
 *   onChange={setTaxExempt}
 *   value={taxExempt}
 * >
 *   {t('customers.customerDetails.taxExempt')}
 * </PosCheckbox>
 * ```
 */
export const PosCheckbox = <TFieldValues extends FieldValues = FieldValues>({
  control,
  name,
  children,
  ...props
}: PosCheckboxProps<TFieldValues>) => {
  const isControlled = !!control;
  const { posFormItemProps, rest: checkboxProps } =
    extractPosFormItemProps(props);

  if (isControlled)
    return (
      <PosFormItem
        control={control}
        name={name}
        valuePropName="checked"
        {...posFormItemProps}
      >
        <Controller
          control={control}
          name={name}
          render={({ field: { onChange, value, ...field } }) => (
            <Checkbox
              {...checkboxProps}
              {...field}
              checked={value}
              onChange={onChange}
              data-testid={name}
              aria-label={name}
            >
              {children}
            </Checkbox>
          )}
        />
      </PosFormItem>
    );

  return <Checkbox {...props}>{children}</Checkbox>;
};
